// After Stripe Checkout (success_url): the webhook grants the work on the server, usually within
// seconds. This page asks GET /v1/entitlements every two seconds until something new is there, then
// says so and opens the way to it. If it takes longer than a minute, it says that calmly too.
// Set as a plate (a finished step): the book itself, standing, once it's known.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { LinkButton } from "../../components/Button";
import { ShelfBook } from "../../components/library/Shelf";
import { Plate } from "../../components/Surface";
import ui from "../../components/ui.module.css";
import { entitlementsBeforeCheckout, useEntitlements } from "../../data/catalogueSource";
import { toneOf, useAuthors, useCatalogue } from "../../data/works";
import { useTitle } from "../../lib/useTitle";
import s from "./library.module.css";

export const Route = createFileRoute("/_app/library/purchased")({
  validateSearch: (q: Record<string, unknown>): { session_id?: string } => (typeof q.session_id === "string" && q.session_id.length < 300 ? { session_id: q.session_id } : {}),
  component: Purchased,
});

const PATIENCE = 60_000;

function Purchased() {
  useTitle("Thank you");
  const [timedOut, setTimedOut] = useState(false);
  // What the reader had before checkout (kept when they left for Stripe), else what was there when
  // this page first heard: anything beyond it is the purchase.
  const [first, setFirst] = useState<Set<string> | null>(() => entitlementsBeforeCheckout());
  const [unlocked, setUnlocked] = useState(false);
  const { data: ents } = useEntitlements({ poll: unlocked || timedOut ? false : 2000 });
  const { data: catalogue } = useCatalogue();
  const { data: authors } = useAuthors();
  if (ents && !first) setFirst(new Set(ents.map((e) => e.key)));
  const added = first ? (ents?.filter((e) => !first.has(e.key)) ?? []) : [];
  if (added.length > 0 && !unlocked) setUnlocked(true);
  const work = catalogue?.works.find((w) => added.some((e) => e.key === w.id || w.productIds.some((p) => p.key === e.key)));
  const waiting = !unlocked && !timedOut;

  useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), PATIENCE);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={ui.page}>
      <Plate pad="lg" className={s.donePlate}>
        <div className={s.done} aria-live="polite">
          {unlocked ? (
            <>
              {work && <ShelfBook id={work.id} title={work.title} shortTitle={work.shortTitle} author={work.author} tone={toneOf(work, authors)} kind={work.kind} height={176} />}
              <h1 className={s.doneTitle}>It’s in your library.</h1>
              <p className={s.doneText}>Thank you. It opens here and in the Bible Standard app, on every device you sign in to.</p>
              {work ? (
                <LinkButton to="/books/$id" params={{ id: work.id }} variant="primary" size="lg" arrow>
                  {`Open ${work.shortTitle}`}
                </LinkButton>
              ) : (
                <LinkButton to="/library" variant="primary" size="lg" arrow>
                  Go to the library
                </LinkButton>
              )}
            </>
          ) : waiting ? (
            <>
              <h1 className={s.doneTitle}>Adding it to your library…</h1>
              <p className={s.doneText}>Thank you. This usually takes a few seconds.</p>
              <span className={ui.spinner} aria-hidden="true" />
            </>
          ) : (
            <>
              <h1 className={s.doneTitle}>It’s taking longer than usual.</h1>
              <p className={s.doneText}>Your payment went through. The work will appear in your library shortly; you don’t need to stay on this page.</p>
              <LinkButton to="/library">Go to the library</LinkButton>
            </>
          )}
        </div>
      </Plate>
    </div>
  );
}
